import { useCallback, useRef } from 'react';
import isEmpty from 'lodash/isEmpty';

import AdActions from 'app/shared/flux/actions/AdActions';
import AreaActions from 'app/shared/flux/actions/AreaActions';
import ErrorActions from 'app/shared/flux/actions/ErrorActions';
import { listingInCurrentArea } from 'app/shared/templates/SplitMapTemplate/controller';
import routeUtils from 'app/shared/utils/routeUtils';

import type { AppThunkDispatch } from 'app/shared/flux/store/configureStore';
import type { AreaDetails } from 'app/shared/flux/reducers/area';
import type { ListingDetails } from 'app/types/listingDetails.type';
import type { ParsedQueryObject } from 'app/shared/utils/queryUtils';

type UseAreaResolutionParams = {
  area: AreaDetails;
  currentListing: ListingDetails | null;
  dispatch: AppThunkDispatch;
  isMapView: boolean;
  pathname: string;
  query: ParsedQueryObject;
};

type UseAreaResolutionReturn = {
  resolveListingArea: (prevListing: ListingDetails | null) => Promise<void>;
  fetchArea: (areaResourceId?: string) => Promise<void>;
};

/**
 * Resolves the correct area context when navigating to a listing (HDP/BDP).
 *
 * Replaces three tightly-coupled methods from SplitMapTemplateClass:
 *
 * - `fetchArea(areaResourceId?)` — loads an area page by resourceId (or extracts
 *    it from the current URL), then refreshes ads. Now returned as `fetchArea`.
 *
 * - `handleListingPageUpdates(prevProps)` — ran on mount and update to determine
 *    *which* area a listing belongs to. Three resolution strategies (in priority order):
 *    1. Email deep-link with min/max lat/lon → `getBestFitArea` (was `getSavedSearchArea`)
 *    2. Listing not in current area & no breadcrumbs → `getAreaByLatLon` from listing geo
 *    3. Listing not in current area & has breadcrumbs → use breadcrumb[1] or breadcrumb[0]
 *    Now returned as `resolveListingArea`.
 *
 * - `getSavedSearchArea()` — fetched best-fit area from email query params.
 *    Inlined into `resolveListingArea`'s email deep-link branch.
 */
export function useAreaResolution({
  area,
  currentListing,
  dispatch,
  isMapView,
  pathname,
  query,
}: UseAreaResolutionParams): UseAreaResolutionReturn {
  const fetchArea = useCallback(
    (areaResourceId?: string): Promise<void> => {
      const resolvedAreaResourceId = areaResourceId || routeUtils.getResourceIdFromUrl(pathname);

      return dispatch(AreaActions.loadAreaPage(resolvedAreaResourceId, isMapView))
        .then(() => dispatch(AdActions.refreshAd(true)))
        .catch((error: unknown) => {
          dispatch(
            ErrorActions.errorHandler({
              errorLocation: 'component.splitMapTemplate.fetchArea',
              errorClass: 'areaActions',
              error: error as Error,
            }),
          );
        });
    },
    [dispatch, isMapView, pathname],
  );

  const resolveListingArea = useCallback(
    (prevListing: ListingDetails | null): Promise<void> => {
      const listingFromEmail = routeUtils.hasValidMinMaxLatLon(query);
      const thisPropsMlid = (currentListing || ({} as Record<string, unknown>)).maloneLotIdEncoded;
      const prevPropsMlid = (prevListing || ({} as Record<string, unknown>)).maloneLotIdEncoded;

      if (currentListing && thisPropsMlid !== prevPropsMlid && thisPropsMlid) {
        const inArea = listingInCurrentArea({
          resourceId: area.resourceId,
          currentListing,
        });

        if (listingFromEmail) {
          return dispatch(
            AreaActions.getBestFitArea({
              minLat: query.minLat,
              maxLat: query.maxLat,
              minLon: query.minLon,
              maxLon: query.maxLon,
            }),
          )
            .then((result: { resourceId: string }) => fetchArea(result.resourceId))
            .catch((error: unknown) => {
              dispatch(
                ErrorActions.errorHandler({
                  error: error as Error,
                  errorLocation: 'component.splitMapTemplate.handleListingPageUpdates',
                  errorClass: 'areaActions',
                }),
              );
            });
        } else if (!inArea || isEmpty(area)) {
          if (isEmpty(currentListing.areas.breadcrumbs)) {
            return (
              dispatch(
                AreaActions.getAreaByLatLon({
                  lat: currentListing.geo.lat,
                  lon: currentListing.geo.lon,
                }),
              ) as Promise<{ resourceId: string }>
            )
              .then((areaResult = {} as { resourceId: string }) => fetchArea(areaResult.resourceId))
              .catch((error: unknown) => {
                dispatch(
                  ErrorActions.errorHandler({
                    error: error as Error,
                    errorLocation: 'component.splitMapTemplate.handleListingPageUpdates',
                    errorClass: 'areaActions',
                  }),
                );
              });
          } else {
            const areaResourceId =
              currentListing.areas.breadcrumbs.length > 1
                ? currentListing.areas.breadcrumbs[1].resourceId
                : currentListing.areas.breadcrumbs[0].resourceId;
            return fetchArea(areaResourceId).catch((error: unknown) => {
              dispatch(
                ErrorActions.errorHandler({
                  error: error as Error,
                  errorLocation: 'component.splitMapTemplate.handleListingPageUpdates',
                  errorClass: 'areaActions',
                }),
              );
            });
          }
        }
      }
      return Promise.resolve();
    },
    [area, currentListing, dispatch, fetchArea, query],
  );

  return { resolveListingArea, fetchArea };
}
